/**
 * The Decision Room — the executive's desk over the platform's records.
 *
 * The Decision Making Assist engine (`decision.ts`) answers the maintenance
 * planner's question: which findings get the quarter's windows. An executive's
 * questions are larger and fewer — take the runway or work it in nights, how
 * much capital to commit, whether the airport is ready for the peak, what to
 * put in front of the regulator. This module finds those decisions in the
 * records rather than asking anyone to list them, frames each one the same
 * way, stress-tests every option against the same five scenarios, and says
 * at what input the recommendation would flip.
 *
 * The rule of the platform holds: every figure is computed from a record, a
 * rate the airport entered, or a value a named person typed, and it says
 * which. A model may explain and challenge; it never produces a number.
 *
 * Pure. The page gathers, the room decides.
 */
import type { ClosureCost, CriticalReport } from "./capacity";
import type { Closure } from "./closures";
import type { ComplianceReport } from "./compliance";
import {
  runEngine,
  type Constraints,
  type DecisionItem,
  type EngineInputs,
  type EngineOutput,
  type SourceRef,
} from "./decision";
import { money } from "./intelligence";
import type { Kpi } from "./kpis";
import type { LiabilityCurve } from "./liability";
import type { Programme } from "./programme";
import type { WorkOrder } from "./workOrders";
import { t, tn } from "../i18n";

/* ── shapes ───────────────────────────────────────────────────────────── */

export type FrameKey = "runway" | "capital" | "peak" | "regulator" | "quarter";
export type Door = "one_way" | "two_way";
export type Verdict = "holds" | "strained" | "fails";

/** A number on the desk, with where it came from. */
export interface Stake {
  key: string;
  label: string;
  value: number | null;
  unit: string;
  /** the sentence the number sits in */
  text: string;
  /** true when the figure rests on a rate card or an entered value */
  assumed?: boolean;
  sources: SourceRef[];
}

export interface RoomOption {
  key: string;
  name: string;
  meaning: string;
  /** cash out, in the site currency; null = not priced, and `unpriced` says why */
  cost: number | null;
  /** hours the runway, taxiway or stand is out of use */
  closureHours: number | null;
  /** regulatory exposure left after the option, 0 (none) … 1 (a crossed limit left open) */
  exposure: number;
  /** calendar days the option takes; null when it has no timeline */
  days: number | null;
  door: Door;
  unpriced?: string;
  metrics: { label: string; value: string; assumed?: boolean }[];
  /** what taking this option pushes out */
  displaces: string[];
  forCase: string[];
  againstCase: string[];
  sources: SourceRef[];
}

export interface Scenario {
  key: string;
  name: string;
  what: string;
  costX: number;
  closureX: number;
  /** hours added to every option's closure — an unplanned failure during the works */
  addClosureHours: number;
  /** an option costing more than this fails outright */
  budgetCap?: number;
  /** an option leaving more exposure than this fails outright */
  exposureCap?: number;
  /** days added to every option's timeline; an option then past the decide-by date fails */
  addDays: number;
  sources: SourceRef[];
}

export interface StressCell {
  option: string;
  scenario: string;
  verdict: Verdict;
  /** cash-equivalent under the scenario over the option's own baseline — how much the scenario hurts it; null when unpriced */
  ratio: number | null;
  text: string;
}

export interface Flip {
  input: string;
  now: string;
  at: string;
  outcome: string;
}

export interface RoomDecision {
  id: string;
  frame: FrameKey;
  title: string;
  question: string;
  /** why it is on the desk at all */
  onDesk: string;
  decideBy: { at?: string; days: number | null; text: string; sources: SourceRef[] };
  /** when the work itself has to be finished — the slip scenario is judged against this */
  horizon: { days: number | null; text: string };
  door: Door;
  doorWhy: string;
  stakes: Stake[];
  options: RoomOption[];
  recommended: string | null;
  reasoning: string[];
  counter: string;
  scenarios: Scenario[];
  stress: StressCell[];
  robust: string | null;
  robustNote: string;
  /** what else moves when the recommended option is taken */
  ripples: string[];
  flips: Flip[];
  boardLine: string[];
  evidence: SourceRef[];
  gaps: string[];
}

/** What the executive typed that the platform does not hold. */
export interface RoomEntered {
  peakName?: string;
  peakFrom?: string;
  peakTo?: string;
  auditAt?: string;
  /** an estimate for an option the platform could not price: by option key */
  estimates?: Record<string, { cost?: number; closureHours?: number; days?: number }>;
  enteredBy?: string;
  enteredAt?: string;
}

export interface RoomInputs {
  now: Date;
  siteCode: string;
  engine: EngineInputs;
  out: EngineOutput;
  curve: LiabilityCurve | null;
  programme: Programme | null;
  critical: CriticalReport | null;
  kpis: Kpi[];
  compliance: ComplianceReport | null;
  closures: Closure[];
  closureCosts: Record<string, ClosureCost | null>;
  workOrders: WorkOrder[];
  entered: RoomEntered;
}

export interface Desk {
  generatedAt: string;
  siteCode: string;
  currency: string;
  decisions: RoomDecision[];
  summary: { open: number; oneWay: number; nearestDays: number | null; moneyInPlay: number };
  /** every number the room emitted, for the figure check on model text */
  figures: Set<string>;
}

/* ── helpers ──────────────────────────────────────────────────────────── */

const DAY = 86_400_000;
const r1 = (n: number) => Math.round(n * 10) / 10;
const r0 = (n: number) => Math.round(n);
const days = (from: Date, to: string) => Math.ceil((new Date(to).getTime() - from.getTime()) / DAY);
const iso = (d: Date, plus: number) => new Date(d.getTime() + plus * DAY).toISOString();
const hours = (min: number | undefined) => (min == null ? null : r1(min / 60));
const dedupe = (refs: SourceRef[]): SourceRef[] => {
  const seen = new Set<string>();
  return refs.filter((r) => {
    const k = `${r.kind}:${r.ref}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};
const rateRef = (c: Constraints | undefined, card: EngineInputs["rateCard"], what: string): SourceRef => ({
  kind: "rate_card",
  ref: "rate card",
  label: what,
  enteredBy: card.enteredBy,
  enteredAt: card.enteredAt ?? c?.quarter,
});
const enteredRef = (e: RoomEntered, what: string): SourceRef => ({
  kind: "entered",
  ref: "entered",
  label: what,
  enteredBy: e.enteredBy,
  enteredAt: e.enteredAt,
});

/** Regulatory exposure of one engine item as the engine scored it, else 1 for a mandatory item. */
function exposureOf(i: DecisionItem): number {
  const r = i.score.components.find((c) => c.key === "regulatoryExposure")?.value;
  return i.mandatory.is ? 1 : (r ?? 0);
}

/* ── the stress test, shared by every frame ───────────────────────────── */

function scenarios(inp: RoomInputs, horizonDays: number | null, budget: number): Scenario[] {
  const unplanned = inp.closures
    .filter((c) => c.kind === "unplanned" && c.minutes)
    .sort((a, b) => (b.minutes ?? 0) - (a.minutes ?? 0))[0];
  const p1Hours = unplanned?.minutes ? r1(unplanned.minutes / 60) : 4;
  return [
    {
      key: "peak",
      name: t("Peak season"),
      what: t(
        "every hour of closure costs two and a half times as much in movements — the stress an Umrah or Hajj peak puts on the same works",
      ),
      costX: 1,
      closureX: 2.5,
      addClosureHours: 0,
      addDays: 0,
      sources: [],
    },
    {
      key: "cut",
      name: t("Budget cut of 20%"),
      what: t("the envelope drops to {amount}; an option costing more than that cannot be funded", {
        amount: money(r0(budget * 0.8), inp.engine.rateCard.currency),
      }),
      costX: 1,
      closureX: 1,
      addClosureHours: 0,
      budgetCap: r0(budget * 0.8),
      addDays: 0,
      sources: [],
    },
    {
      key: "p1",
      name: t("An unplanned failure mid-works"),
      what: unplanned
        ? t("{hours} h of unplanned closure lands on top of the works — the longest on record, {ref} on {zone}", {
            hours: p1Hours,
            ref: unplanned.reference ?? unplanned.id,
            zone: unplanned.zoneCode ?? t("the airfield"),
          })
        : t(
            "{hours} h of unplanned closure lands on top of the works (no unplanned closure on record; a starting value)",
            { hours: p1Hours },
          ),
      costX: 1.1,
      closureX: 1,
      addClosureHours: p1Hours,
      addDays: 0,
      sources: unplanned
        ? [{ kind: "closure", ref: unplanned.reference ?? unplanned.id, label: unplanned.reason, href: "/capacity" }]
        : [],
    },
    {
      key: "audit",
      name: t("The regulator arrives"),
      what: t(
        "an inspection lands before the option is complete: anything that leaves a crossed limit or an overdue obligation open fails",
      ),
      costX: 1,
      closureX: 1,
      addClosureHours: 0,
      exposureCap: 0.45,
      addDays: 0,
      sources: [],
    },
    {
      key: "slip",
      name: t("A 30-day slip"),
      what:
        horizonDays != null
          ? t(
              "the contractor or the crew is a month late; an option that then runs past the horizon of {days} days is strained, past twice it fails",
              { days: horizonDays },
            )
          : t("the contractor or the crew is a month late"),
      costX: 1.1,
      closureX: 1,
      addClosureHours: 0,
      addDays: 30,
      sources: [],
    },
  ];
}

/** Cash-equivalent of an option: cash out plus its closure hours priced at the rate card. */
function cashEq(o: RoomOption, rate: number, costX = 1, closureX = 1, addClosure = 0): number | null {
  if (o.cost == null) return null;
  return o.cost * costX + ((o.closureHours ?? 0) * closureX + addClosure) * rate;
}

function stressTest(
  options: RoomOption[],
  scen: Scenario[],
  rate: number,
  horizonDays: number | null,
  currency: string,
): { cells: StressCell[]; robust: string | null; robustNote: string } {
  const cells: StressCell[] = [];
  const worst: Record<string, number> = {};
  for (const s of scen) {
    /* the hard rules first: an option that breaks one fails whatever its arithmetic */
    const hard = (o: RoomOption): string | null => {
      if (o.cost == null) return null;
      if (s.budgetCap != null && o.cost * s.costX > s.budgetCap)
        return t("{cost} is over the cap of {cap}", {
          cost: money(r0(o.cost * s.costX), currency),
          cap: money(s.budgetCap, currency),
        });
      if (s.exposureCap != null && o.exposure > s.exposureCap)
        return t("leaves exposure {exposure} open when the inspector arrives", { exposure: o.exposure });
      if (s.addDays && o.days != null && horizonDays != null && o.days + s.addDays > horizonDays * 2)
        return t("{days} days runs past twice the {horizon}-day horizon", {
          days: o.days + s.addDays,
          horizon: horizonDays,
        });
      return null;
    };
    const eqs = options.map((o) => ({
      o,
      eq: cashEq(o, rate, s.costX, s.closureX, s.addClosureHours),
      base: cashEq(o, rate),
      fail: hard(o),
    }));
    for (const { o, eq, base, fail } of eqs) {
      let verdict: Verdict;
      let text: string;
      /* how much the scenario hurts this option, against its own baseline */
      const ratio = eq != null && base != null && base > 0 ? r1(eq / base) : null;
      if (eq == null) {
        verdict = "strained";
        text = t("not priced — cannot be tested");
      } else if (fail) {
        verdict = "fails";
        text = fail;
      } else if (s.addDays && o.days != null && horizonDays != null && o.days + s.addDays > horizonDays) {
        verdict = "strained";
        text = t("{days} days runs past the {horizon}-day horizon", { days: o.days + s.addDays, horizon: horizonDays });
      } else {
        /* a fail is a rule broken; the arithmetic alone only separates holds from strained */
        verdict = ratio == null ? "strained" : ratio <= 1.25 ? "holds" : "strained";
        text = t("{amount} cash-equivalent, {ratio}× its own baseline", {
          amount: money(r0(eq), currency),
          ratio: ratio ?? "n/a",
        });
      }
      cells.push({ option: o.key, scenario: s.key, verdict, ratio, text });
      const pain = verdict === "fails" || eq == null ? Infinity : eq;
      worst[o.key] = Math.max(worst[o.key] ?? 0, pain);
    }
  }
  const ranked = options
    .map((o) => ({ key: o.key, worst: worst[o.key] ?? Infinity }))
    .sort((a, b) => a.worst - b.worst);
  const top = ranked[0];
  if (!top || !Number.isFinite(top.worst))
    return {
      cells,
      robust: null,
      robustNote: t("No option survives every scenario; whichever is chosen needs a fallback written now."),
    };
  const name = options.find((o) => o.key === top.key)?.name ?? top.key;
  return {
    cells,
    robust: top.key,
    robustNote: t(
      "{name} has the best worst case: {amount} cash-equivalent in its hardest scenario, and it fails none.",
      { name, amount: money(r0(top.worst), currency) },
    ),
  };
}

/* ── the flip points ──────────────────────────────────────────────────── */

function flips(options: RoomOption[], recommended: string | null, rate: number, currency: string): Flip[] {
  const rec = options.find((o) => o.key === recommended);
  if (!rec || rec.cost == null) return [];
  const others = options.filter((o) => o.key !== rec.key && o.cost != null);
  if (!others.length) return [];
  const out: Flip[] = [];
  const steps = [1.1, 1.25, 1.5, 2, 3, 4, 6, 8];
  /* the closure valuation: at what rate per hour does the recommendation stop winning? */
  const winsAt = (r: number) => {
    const mine = cashEq(rec, r)!;
    return others.every((o) => cashEq(o, r)! >= mine);
  };
  if (winsAt(rate)) {
    for (const dir of [1, -1] as const) {
      for (const m of steps) {
        const r = dir === 1 ? rate * m : rate / m;
        if (!winsAt(r)) {
          const winner = others.sort((a, b) => cashEq(a, r)! - cashEq(b, r)!)[0];
          out.push({
            input: t("the value of an hour of closure"),
            now: t("{amount}/h on the rate card", { amount: money(r0(rate), currency) }),
            at:
              dir === 1
                ? t("above {amount}/h", { amount: money(r0(r), currency) })
                : t("below {amount}/h", { amount: money(r0(r), currency) }),
            outcome: t("{name} wins", { name: winner.name }),
          });
          break;
        }
      }
    }
  }
  /* the recommended option's own cost */
  for (const m of steps) {
    const inflated = { ...rec, cost: rec.cost * m };
    const mine = cashEq(inflated, rate)!;
    const winner = others.filter((o) => cashEq(o, rate)! < mine).sort((a, b) => cashEq(a, rate)! - cashEq(b, rate)!)[0];
    if (winner) {
      out.push({
        input: t('the cost of "{name}"', { name: rec.name }),
        now: money(r0(rec.cost), currency),
        at: t("above {amount}", { amount: money(r0(rec.cost * m), currency) }),
        outcome: t("{name} wins", { name: winner.name }),
      });
      break;
    }
  }
  return out;
}

/* ── recommendation and the board line ────────────────────────────────── */

function recommend(
  options: RoomOption[],
  rate: number,
  currency: string,
): { key: string | null; reasoning: string[]; counter: string } {
  const priced = options.filter((o) => o.cost != null);
  if (!priced.length)
    return {
      key: null,
      reasoning: [t("No option is priced, so the room ranks none; enter an estimate beside the option to rank it.")],
      counter: "",
    };
  const ranked = [...priced].sort((a, b) => cashEq(a, rate)! - cashEq(b, rate)!);
  const best = ranked[0];
  const reasoning = [
    t(
      "Ranked on cash-equivalent: cash out plus closure hours priced at {rate}/h from the rate card. {name}: {amount}.",
      { rate: money(r0(rate), currency), name: best.name, amount: money(r0(cashEq(best, rate)!), currency) },
    ),
    ...ranked
      .slice(1)
      .map(
        (o) =>
          `${o.name}: ${money(r0(cashEq(o, rate)!), currency)}${o.exposure < best.exposure ? t(" — but it leaves less regulatory exposure") : ""}.`,
      ),
  ];
  const unpriced = options.filter((o) => o.cost == null);
  if (unpriced.length)
    reasoning.push(
      unpriced.length === 1
        ? t("{names} is not priced and not ranked: {why}.", {
            names: unpriced.map((o) => o.name).join(t(" and ")),
            why: unpriced.map((o) => o.unpriced).join("; "),
          })
        : t("{names} are not priced and not ranked: {why}.", {
            names: unpriced.map((o) => o.name).join(t(" and ")),
            why: unpriced.map((o) => o.unpriced).join("; "),
          }),
    );
  const second = ranked[1];
  const counter = second
    ? t("{name} costs {amount} more on this arithmetic{exposure}{hours}. {against}", {
        name: second.name,
        amount: money(r0(cashEq(second, rate)! - cashEq(best, rate)!), currency),
        exposure:
          second.exposure < best.exposure
            ? t(" and leaves exposure {second} against {best}", { second: second.exposure, best: best.exposure })
            : "",
        hours:
          second.closureHours != null && best.closureHours != null && second.closureHours < best.closureHours
            ? t(", and takes {n} fewer hours off the airfield", { n: r1(best.closureHours - second.closureHours) })
            : "",
        against: best.againstCase[0] ?? "",
      })
    : (best.againstCase[0] ?? "");
  return { key: best.key, reasoning, counter };
}

function boardLine(d: Omit<RoomDecision, "boardLine">, currency: string): string[] {
  const rec = d.options.find((o) => o.key === d.recommended);
  const robust = d.options.find((o) => o.key === d.robust);
  const first = `${d.title}: ${d.onDesk}`;
  const second = rec
    ? t("The room recommends {name}{cost}{hours}.", {
        name: rec.name.toLowerCase(),
        cost: rec.cost != null ? t(" at {amount}", { amount: money(r0(rec.cost), currency) }) : "",
        hours: rec.closureHours != null ? t(" and {n} h off the airfield", { n: rec.closureHours }) : "",
      })
    : t("The room makes no recommendation until an option is priced.");
  const third =
    robust && rec && robust.key !== rec.key
      ? t(
          "It is not the robust choice: under stress {robust} holds where {recommended} does not, so decide with the peak and the budget in view.",
          { robust: robust.name.toLowerCase(), recommended: rec.name.toLowerCase() },
        )
      : robust
        ? t("It is also the option that survives every scenario tested.")
        : t("No option survives every scenario; a fallback should be written with the decision.");
  return [first, second, third];
}

/* ── frames ───────────────────────────────────────────────────────────── */

type Draft = Omit<RoomDecision, "scenarios" | "stress" | "robust" | "robustNote" | "flips" | "boardLine">;

function finish(draft: Draft, inp: RoomInputs, rate: number, budget: number): RoomDecision {
  const cur = inp.engine.rateCard.currency;
  const scen = scenarios(inp, draft.horizon.days, budget);
  const st = stressTest(draft.options, scen, rate, draft.horizon.days, cur);
  const fl = flips(draft.options, draft.recommended, rate, cur);
  const partial = {
    ...draft,
    scenarios: scen,
    stress: st.cells,
    robust: st.robust,
    robustNote: st.robustNote,
    flips: fl,
  };
  return { ...partial, boardLine: boardLine(partial, cur), evidence: dedupe(draft.evidence) };
}

/** The runway (or any runway section) the platform says is past or heading for its limit. */
function runwayFrame(inp: RoomInputs): RoomDecision[] {
  const cur = inp.engine.rateCard.currency;
  const card = inp.engine.rateCard;
  const rate = card.closureCostPerHour.runway;
  const items = inp.out.items.filter((i) => i.surface.zoneClass === "runway" && i.category === "pavement" && i.zone);
  const zones = [...new Set(items.map((i) => i.zone!))];
  return zones.map((zone) => {
    const lead = items
      .filter((i) => i.zone === zone)
      .sort((a, b) => (b.mandatory.is ? 1 : 0) - (a.mandatory.is ? 1 : 0))[0];
    const others = inp.out.items.filter((i) => i.zone === zone && i.id !== lead.id);
    const waiting = inp.workOrders.filter(
      (w) => w.zoneCode === zone && ["draft", "approved", "dispatched", "on_hold"].includes(w.status),
    );
    const cc = inp.closureCosts[zone] ?? null;
    const history = inp.closures.filter((c) => c.zoneCode === zone && c.minutes);
    const longest = [...history].sort((a, b) => (b.minutes ?? 0) - (a.minutes ?? 0))[0];
    const nightHours = hours(lead.surface.closureMinutes) ?? 4;
    const fullHours = longest?.minutes ? r1(longest.minutes / 60) : nightHours * 1.5;
    const evidence: SourceRef[] = [...lead.evidence];
    const gaps: string[] = [];

    /* stakes */
    const stakes: Stake[] = [
      {
        key: "condition",
        label: t("condition"),
        value: lead.condition.value ?? null,
        unit: lead.condition.unit ?? "",
        text: lead.condition.label,
        sources: lead.condition.sources,
      },
      {
        key: "ttt",
        label: t("days to the limit"),
        value: lead.timeToThresholdDays,
        unit: t("days"),
        text:
          lead.timeToThresholdDays === 0
            ? t("the limit is already crossed")
            : lead.timeToThresholdDays == null
              ? t("not computable: {basis}", { basis: lead.trend.basis })
              : t("{n} days on the trend ({basis})", { n: lead.timeToThresholdDays, basis: lead.trend.basis }),
        sources: lead.trend.sources,
      },
      {
        key: "movements",
        label: t("movements in a closure"),
        value: cc?.known ? cc.movements : null,
        unit: t("movements"),
        text: cc?.known
          ? t("{movements} movements in the capacity model's {hours} h window: {stranded} stranded, {bussed} bussed", {
              movements: cc.movements,
              hours: cc.hours ?? "—",
              stranded: cc.stranded,
              bussed: cc.bussed,
            })
          : t("the capacity model does not cover the window: {why}", { why: cc?.whyNot ?? t("no schedule loaded") }),
        sources: cc ? [{ kind: "capacity", ref: zone, label: t("closure cost"), href: "/capacity" }] : [],
      },
      {
        key: "hour",
        label: t("an hour of runway closure"),
        value: rate,
        unit: cur,
        text: t("{amount} per hour on the rate card — the figure every option below is judged on", {
          amount: money(rate, cur),
        }),
        assumed: true,
        sources: [rateRef(inp.engine.constraints, card, t("closure cost per hour, runway"))],
      },
      {
        key: "waiting",
        label: t("other work waiting here"),
        value: waiting.length,
        unit: t("work orders"),
        text: waiting.length
          ? `${waiting.map((w) => `${w.number} ${w.title}`).join("; ")}`
          : t("nothing else is waiting on this runway"),
        sources: waiting.map((w) => ({
          kind: "work_order",
          ref: w.number,
          label: w.title,
          href: `/work-orders/${w.id}`,
        })),
      },
    ];
    if (!cc?.known)
      gaps.push(
        t(
          "the capacity model has no schedule for {zone}, so movements in a closure are unknown and the rate card stands in",
          { zone },
        ),
      );

    /* options */
    const bundleCost = others.reduce((t, i) => t + (i.cost.now ?? 0), 0) + (lead.cost.now ?? 0);
    const est = inp.entered.estimates?.["runway:resurface"];
    const optNights: RoomOption = {
      key: "nights",
      name: t("Treat it in night windows"),
      meaning: t("{work} done in the next night window, {hours} h at a time, the runway open by day", {
        work: lead.headline.split(" — ")[0],
        hours: nightHours,
      }),
      cost: lead.cost.now ?? null,
      closureHours: nightHours,
      exposure: 0,
      days: 7,
      door: "two_way",
      metrics: [
        {
          label: t("cost"),
          value: lead.cost.now != null ? money(lead.cost.now, cur) : t("not priced"),
          assumed: lead.cost.sources.every((s) => s.kind === "rate_card"),
        },
        { label: t("closure"), value: t("{n} h, at night", { n: nightHours }) },
        {
          label: t("movements touched"),
          value: cc?.known
            ? t("{touched} of {movements}", { touched: cc.bussed + cc.stranded, movements: cc.movements })
            : t("unknown"),
        },
        { label: t("exposure after"), value: t("0 — the limit is restored") },
      ],
      displaces: inp.out.portfolio.slipped.slice(0, 2).map((s) => s.item.asset),
      forCase: [
        t("Cheapest and quickest; the runway is never closed in daylight."),
        t("Repeatable: the same window serves the next treatment."),
      ],
      againstCase: [t("Treats the symptom: the trend says it returns, and the survey cadence has to be kept.")],
      sources: [...lead.cost.sources, ...lead.surface.sources],
    };
    const optBundle: RoomOption = {
      key: "bundle",
      name: t("One full closure, everything at once"),
      meaning: t("close {zone} once for {hours} h and do the treatment with the {jobs} waiting on it", {
        zone,
        hours: fullHours,
        jobs: tn(others.length + waiting.length, "{n} other job", "{n} other jobs"),
      }),
      cost: bundleCost,
      closureHours: fullHours,
      exposure: 0,
      days: 14,
      door: "one_way",
      metrics: [
        { label: t("cost"), value: money(bundleCost, cur), assumed: true },
        {
          label: t("closure"),
          value: t("{n} h in one closure{longest}", {
            n: fullHours,
            longest: longest ? t(" (the longest on record, {ref})", { ref: longest.reference ?? longest.id }) : "",
          }),
        },
        {
          label: t("movements touched"),
          value: cc?.known ? t("{n} in the window", { n: cc.movements }) : t("unknown"),
        },
        { label: t("exposure after"), value: t("0 — everything on the section is cleared") },
      ],
      displaces: [],
      forCase: [
        t("One mobilisation and one NOTAM instead of several."),
        t("Clears every open item on the runway in one go."),
      ],
      againstCase: [t("A daytime closure is a one-way door once notified; the movements in it are moved or lost.")],
      sources: [
        ...(longest
          ? [
              {
                kind: "closure" as const,
                ref: longest.reference ?? longest.id,
                label: longest.reason,
                href: "/capacity",
              },
            ]
          : []),
        ...waiting.map((w) => ({
          kind: "work_order" as const,
          ref: w.number,
          label: w.title,
          href: `/work-orders/${w.id}`,
        })),
      ],
    };
    const optResurface: RoomOption = {
      key: "resurface",
      name: t("Treat now, resurface the section next year"),
      meaning: t("the interim treatment now, and the section put to the next capital envelope for resurfacing"),
      cost: est?.cost != null ? est.cost + (lead.cost.now ?? 0) : null,
      closureHours: est?.closureHours ?? null,
      exposure: 0,
      days: est?.days ?? null,
      door: "one_way",
      unpriced:
        est?.cost == null
          ? t("the platform holds no priced resurfacing candidate for this section; enter an estimate to rank it")
          : undefined,
      metrics: [
        {
          label: t("cost"),
          value:
            est?.cost != null
              ? t("{estimate} + {interim} interim", {
                  estimate: money(est.cost, cur),
                  interim: money(lead.cost.now ?? 0, cur),
                })
              : t("not priced"),
          assumed: true,
        },
        { label: t("closure"), value: est?.closureHours != null ? `${est.closureHours} h` : t("unknown") },
        { label: t("exposure after"), value: t("0 now; the recurrence ends with the resurfacing") },
        { label: t("door"), value: t("one-way — capital committed") },
      ],
      displaces: inp.curve ? [t("a share of the FY{year} envelope", { year: inp.curve.fiscalYear })] : [],
      forCase: [t("Ends the recurrence instead of treating it each quarter.")],
      againstCase: [t("Commits capital the register has not priced; the estimate is the whole argument.")],
      sources: est ? [enteredRef(inp.entered, t("resurfacing estimate"))] : [],
    };
    const options = [optNights, optBundle, optResurface];
    evidence.push(...options.flatMap((o) => o.sources));

    const decideBy = lead.mandatory.is
      ? {
          days: 14,
          at: iso(inp.now, 14),
          text: t("the limit is crossed: the treatment has to be in the next fortnight's windows"),
          sources: lead.clause.sources,
        }
      : lead.timeToThresholdDays != null
        ? {
            days: lead.timeToThresholdDays,
            at: iso(inp.now, lead.timeToThresholdDays),
            text: t("{n} days to the limit on the trend", { n: lead.timeToThresholdDays }),
            sources: lead.trend.sources,
          }
        : {
            days: null,
            text: t("no trend to date it: {basis}", { basis: lead.trend.basis }),
            sources: lead.trend.sources,
          };
    const { key, reasoning, counter } = recommend(options, rate, cur);
    const draft: Draft = {
      id: `runway:${zone}`,
      frame: "runway",
      title: t("{zone} below its limit", { zone: zone.replace(/-/g, " ") }),
      question: t(
        "{zone} is {condition}. Treat it in nights, take the runway once for everything, or commit to resurfacing?",
        { zone: zone.replace(/-/g, " "), condition: lead.condition.label },
      ),
      onDesk: lead.mandatory.is
        ? t("{why} — the work is not a decision, how to do it is.", { why: String(lead.mandatory.why) })
        : `${lead.headline} — ${lead.trend.basis}.`,
      decideBy,
      horizon: lead.mandatory.is
        ? { days: 30, text: t("a crossed limit is treated within the month") }
        : {
            days: lead.timeToThresholdDays,
            text:
              lead.timeToThresholdDays != null
                ? t("{n} days to the limit", { n: lead.timeToThresholdDays })
                : t("no trend to date it"),
          },
      door: "two_way",
      doorWhy: t("the treatment can be repeated; only the full closure and the capital commitment are one-way"),
      stakes,
      options,
      recommended: key,
      reasoning,
      counter,
      ripples: [
        ...(optNights.displaces.length
          ? [
              t("a night window taken here is one fewer for {assets} this quarter", {
                assets: optNights.displaces.join(t(" and ")),
              }),
            ]
          : []),
        ...(others.length
          ? [
              t("{items} sit on the same runway and can share the closure", {
                items: others.map((o) => o.headline.split(" — ")[0]).join("; "),
              }),
            ]
          : []),
        ...(cc?.known && cc.bussed
          ? [
              t(
                "{n} movements are bussed in a closure of the capacity model's window — a passenger cost the rate card does not carry",
                { n: cc.bussed },
              ),
            ]
          : []),
      ],
      evidence,
      gaps,
    };
    return finish(draft, inp, rate, inp.engine.constraints.budget);
  });
}

/** Next year's capital: what each envelope buys and what waiting costs. */
function capitalFrame(inp: RoomInputs): RoomDecision[] {
  const curve = inp.curve;
  if (!curve || !curve.available || !curve.scenarios.length) return [];
  const cur = curve.currency || inp.engine.rateCard.currency;
  const card = inp.engine.rateCard;
  const rate = r0((card.closureCostPerHour.taxiway + card.closureCostPerHour.apron) / 2);
  const src: SourceRef = {
    kind: "liability",
    ref: `FY${curve.fiscalYear}`,
    label: t("deferred-cost curve"),
    href: "/liability",
  };
  const options: RoomOption[] = curve.scenarios.map((s) => ({
    key: `env:${s.envelope}`,
    name: t("Fund {amount}", { amount: money(s.envelope, cur) }),
    meaning: t("{funded} of {priced} priced candidates funded, {deferred} deferred", {
      funded: s.funded,
      priced: curve.priced,
      deferred: s.deferred,
    }),
    cost: s.fundedCost + s.costOfWaitingOneYear,
    closureHours: s.closureHoursPerYear,
    exposure: curve.priced ? r1(s.deferred / curve.priced) : 0,
    days: null,
    door: s.deferred === 0 ? "one_way" : "two_way",
    metrics: [
      { label: t("cash out"), value: money(s.fundedCost, cur) },
      { label: t("cost of waiting a year"), value: money(s.costOfWaitingOneYear, cur) },
      {
        label: t("closure hours a year, deferred work"),
        value: t("{hours} h (known for {known}, unknown for {unknown})", {
          hours: s.closureHoursPerYear,
          known: s.closureKnownFor,
          unknown: s.closureUnknownFor,
        }),
      },
      { label: t("deferred"), value: s.deferredDetail.map((d) => d.title).join("; ") || t("nothing") },
    ],
    displaces: s.deferredDetail.map((d) => d.title),
    forCase:
      s.deferred === 0
        ? [t("Nothing deferred: no escalation and no closure hours carried into the year after.")]
        : [
            t("Keeps {amount} of capital uncommitted.", {
              amount: money(curve.scenarios[curve.scenarios.length - 1].envelope - s.envelope, cur),
            }),
          ],
    againstCase:
      s.deferred === 0
        ? [t("The largest cash call, in a year the envelope may be cut.")]
        : [
            t("Waiting costs {amount} and {hours} closure hours the operation pays for.", {
              amount: money(s.costOfWaitingOneYear, cur),
              hours: s.closureHoursPerYear,
            }),
          ],
    sources: [src],
  }));
  const stakes: Stake[] = [
    {
      key: "candidates",
      label: t("candidates"),
      value: curve.candidates,
      unit: "",
      text: t("{candidates} candidates on the register, {priced} priced — {note}", {
        candidates: curve.candidates,
        priced: curve.priced,
        note: curve.coverageNote ?? "",
      }),
      sources: [src],
    },
    {
      key: "wait",
      label: t("cost of waiting"),
      value: curve.scenarios[0].costOfWaitingOneYear,
      unit: cur,
      text: t("{amount} a year at the lowest envelope", {
        amount: money(curve.scenarios[0].costOfWaitingOneYear, cur),
      }),
      sources: [src],
    },
    {
      key: "closure",
      label: t("closure hours deferred work costs"),
      value: curve.scenarios[0].closureHoursPerYear,
      unit: "h/yr",
      text: t("{hours} h a year at the lowest envelope, priced here at {rate}/h", {
        hours: curve.scenarios[0].closureHoursPerYear,
        rate: money(rate, cur),
      }),
      assumed: true,
      sources: [src, rateRef(inp.engine.constraints, card, t("closure cost per hour, taxiway and apron"))],
    },
    ...(inp.programme?.available
      ? [
          {
            key: "blocked",
            label: t("held up by a missing fact"),
            value: inp.programme.totals.blockedByMissingFacts,
            unit: cur,
            text: t("{amount} of priced, funded work is blocked by a missing fact, not by money", {
              amount: money(inp.programme.totals.blockedByMissingFacts, cur),
            }),
            sources: [
              {
                kind: "liability" as const,
                ref: `programme FY${inp.programme.fiscalYear}`,
                label: t("the program"),
                href: "/programme",
              },
            ],
          },
        ]
      : []),
  ];
  const { key, reasoning, counter } = recommend(options, rate, cur);
  const top = curve.scenarios[curve.scenarios.length - 1].envelope;
  const draft: Draft = {
    id: `capital:${curve.fiscalYear}`,
    frame: "capital",
    title: t("The FY{year} envelope", { year: curve.fiscalYear }),
    question: t("How much capital goes to the register next year: {envelopes}?", {
      envelopes: curve.scenarios.map((s) => money(s.envelope, cur)).join(", "),
    }),
    onDesk: t(
      "{priced} priced candidates want {amount}; every year of waiting costs {waiting} at the lowest envelope.",
      {
        priced: curve.priced,
        amount: money(curve.scenarios[curve.scenarios.length - 1].fundedCost, cur),
        waiting: money(curve.scenarios[0].costOfWaitingOneYear, cur),
      },
    ),
    decideBy: { days: 90, at: iso(inp.now, 90), text: t("before the budget is set for the year"), sources: [] },
    horizon: { days: 365, text: t("the fiscal year") },
    door: "one_way",
    doorWhy: t("escalation on deferred work is not recovered by funding it later"),
    stakes,
    options,
    recommended: key,
    reasoning: [
      ...reasoning,
      t(
        "Cash-equivalent here is cash out plus the cost of waiting a year plus the deferred closure hours at {rate}/h.",
        { rate: money(rate, cur) },
      ),
      ...(curve.notPriced
        ? [
            t("{n} candidates are unpriced and in none of these figures: {detail}.", {
              n: curve.notPriced,
              detail: curve.notPricedDetail.map((u) => `${u.title} (${u.whyNot})`).join("; "),
            }),
          ]
        : []),
    ],
    counter,
    ripples: [
      ...(inp.programme?.available && inp.programme.totals.undecidable
        ? [
            t("{n} candidates cannot be scheduled whatever the envelope until someone supplies a fact: {detail}", {
              n: inp.programme.totals.undecidable,
              detail: inp.programme.undecidable.map((u) => `${u.title} — ${u.whoSupplies}`).join("; "),
            }),
          ]
        : []),
      t("the top envelope of {top} funds everything priced and leaves {left} unspent", {
        top: money(top, cur),
        left: money(Math.max(0, top - curve.scenarios[curve.scenarios.length - 1].fundedCost), cur),
      }),
    ],
    evidence: [src],
    gaps: curve.notPriced
      ? [t("{n} of {candidates} candidates carry no price", { n: curve.notPriced, candidates: curve.candidates })]
      : [],
  };
  return [finish(draft, inp, rate, top)];
}

/** Ready for the peak: what is open, what is out of service, and what the quarter's works do to it. */
function peakFrame(inp: RoomInputs): RoomDecision[] {
  const cur = inp.engine.rateCard.currency;
  const card = inp.engine.rateCard;
  const rate = card.closureCostPerHour.runway;
  const e = inp.entered;
  const peakFrom = e.peakFrom ?? iso(inp.now, 45);
  const peakName = e.peakName ?? t("the next peak");
  const assumedPeak = e.peakFrom == null;
  const daysTo = days(inp.now, peakFrom);
  const mandatory = inp.out.portfolio.mandatory;
  const chosen = inp.out.portfolio.chosen;
  const openHigh = inp.engine.report.findings.filter((f) => f.phase === "now" && f.severity >= 4);
  const outages = inp.kpis.find((k) => k.key === "outages");
  const availability = inp.kpis.find((k) => k.key === "runway_availability_pct");
  const crit = inp.critical;
  const overdue = inp.compliance?.obligations.filter((o) => o.status === "overdue") ?? [];
  const closureHoursOf = (items: DecisionItem[]) =>
    r1(items.reduce((t, i) => t + (i.surface.closureMinutes ?? 0) / 60, 0));
  const costOf = (items: DecisionItem[]) => items.reduce((t, i) => t + (i.cost.now ?? 0), 0);
  const slipCost = (items: DecisionItem[]) =>
    items.reduce((t, i) => t + ((i.cost.quarter ?? i.cost.now ?? 0) - (i.cost.now ?? 0)), 0);
  const discretionary = chosen.filter((i) => !i.mandatory.is);
  const stakes: Stake[] = [
    {
      key: "days",
      label: t("days to the peak"),
      value: daysTo,
      unit: t("days"),
      text: t("{peak} starts {date}{assumed}", {
        peak: peakName,
        date: peakFrom.slice(0, 10),
        assumed: assumedPeak ? t(" — a starting value; enter the real date") : "",
      }),
      assumed: assumedPeak,
      sources: assumedPeak ? [] : [enteredRef(e, t("peak dates"))],
    },
    {
      key: "open",
      label: t("open at severity 4 or 5"),
      value: openHigh.length,
      unit: t("findings"),
      text: openHigh.map((f) => f.headline.split(" — ")[0]).join("; ") || t("nothing open at severity 4 or 5"),
      sources: openHigh
        .flatMap((f) => f.prediction.evidence)
        .map((x) => ({ kind: x.kind, ref: x.ref, label: x.label, href: x.href })),
    },
    {
      key: "outages",
      label: t("major equipment out of service"),
      value: outages?.value ?? null,
      unit: outages?.unit ?? t("units"),
      text: outages
        ? t("{value} {unit} against a target of {target} ({source})", {
            value: outages.value ?? "n/a",
            unit: outages.unit,
            target: outages.target ?? "—",
            source: outages.source ?? "KPI",
          })
        : t("no equipment KPI on the platform"),
      sources: outages ? [{ kind: "ledger", ref: "kpi outages", label: outages.label, href: "/executive-kpis" }] : [],
    },
    {
      key: "critical",
      label: t("positions that strand traffic"),
      value: crit?.known ? crit.critical.length : null,
      unit: t("positions"),
      text: crit?.known
        ? t("{critical}; flexible: {flexible}", {
            critical:
              crit.critical
                .map((p) => t("{position} ({n} stranded)", { position: p.position, n: p.stranded }))
                .join(", ") || t("none"),
            flexible: crit.flexible.map((p) => p.position).join(", ") || t("none"),
          })
        : t("unknown: {why}", { why: crit?.whyNot ?? t("no capacity model") }),
      sources: crit
        ? [{ kind: "capacity", ref: "critical positions", label: t("the next 90 days"), href: "/capacity" }]
        : [],
    },
    {
      key: "overdue",
      label: t("obligations overdue"),
      value: overdue.length,
      unit: "",
      text: overdue.map((o) => o.title).join("; ") || t("none overdue"),
      sources: overdue.map((o) => ({
        kind: "obligation",
        ref: o.periodKey ?? o.title,
        label: o.title,
        href: "/compliance",
      })),
    },
  ];
  const gaps: string[] = [];
  if (availability && availability.value == null)
    gaps.push(
      t("runway availability is not computable: {why}", { why: availability.reason ?? t("no operational periods") }),
    );
  if (!crit?.known) gaps.push(t("the capacity model does not cover the peak window"));

  const optSprint: RoomOption = {
    key: "sprint",
    name: t("Readiness sprint"),
    meaning: t(
      "clear the {n} mandatory items and the severity 4–5 findings before {peak}, and nothing else airside until it ends",
      { n: mandatory.length, peak: peakName },
    ),
    cost: costOf(mandatory) + slipCost(discretionary),
    closureHours: closureHoursOf(mandatory),
    exposure: 0,
    days: Math.max(7, mandatory.filter((i) => i.window.needsNight).length * 3),
    door: "two_way",
    metrics: [
      {
        label: t("cost"),
        value: t("{now} now + {slip} of slip on what waits", {
          now: money(costOf(mandatory), cur),
          slip: money(slipCost(discretionary), cur),
        }),
        assumed: true,
      },
      { label: t("closure"), value: t("{n} h, all before the peak", { n: closureHoursOf(mandatory) }) },
      {
        label: t("windows"),
        value: t("{used} of {total}", {
          used: mandatory.filter((i) => i.window.needsNight).length,
          total: inp.out.portfolio.available.windows,
        }),
      },
      { label: t("exposure after"), value: t("0 — nothing mandatory is open when the peak starts") },
    ],
    displaces: discretionary.map((i) => i.asset),
    forCase: [t("The peak starts with no crossed limit and no overdue obligation on the books.")],
    againstCase: [t("Everything discretionary slips a quarter and escalates.")],
    sources: mandatory.flatMap((i) => i.cost.sources),
  };
  const optPlanned: RoomOption = {
    key: "planned",
    name: t("Run the quarter as solved"),
    meaning: t("the solver's {n} items go ahead on their windows, some of them inside the peak", { n: chosen.length }),
    cost: inp.out.portfolio.used.budget,
    closureHours: closureHoursOf(chosen),
    exposure: r1(
      inp.out.portfolio.slipped.reduce((t, s) => t + exposureOf(s.item), 0) / Math.max(1, inp.out.items.length),
    ),
    days: 90,
    door: "two_way",
    metrics: [
      { label: t("cost"), value: money(inp.out.portfolio.used.budget, cur) },
      { label: t("closure"), value: t("{n} h across the quarter", { n: closureHoursOf(chosen) }) },
      {
        label: t("windows"),
        value: t("{used} of {total}", {
          used: inp.out.portfolio.used.windows,
          total: inp.out.portfolio.available.windows,
        }),
      },
      {
        label: t("exposure after"),
        value: t("{n} left on the slipped set", {
          n: r1(inp.out.portfolio.slipped.reduce((sum, s) => sum + exposureOf(s.item), 0)),
        }),
      },
    ],
    displaces: inp.out.portfolio.slipped.map((s) => s.item.asset),
    forCase: [t("The most work done for the money; nothing waits that the solver could fit.")],
    againstCase: [t("Night closures run through the peak, when an hour of closure costs the most.")],
    sources: chosen.flatMap((i) => i.cost.sources).slice(0, 6),
  };
  const optFreeze: RoomOption = {
    key: "freeze",
    name: t("Freeze airside works through the peak"),
    meaning: t("the mandatory items before {peak}, then no planned closure until it ends; everything else after", {
      peak: peakName,
    }),
    cost: costOf(mandatory) + slipCost(chosen.filter((i) => !i.mandatory.is)),
    closureHours: closureHoursOf(mandatory),
    exposure: r1(Math.min(1, discretionary.reduce((t, i) => t + exposureOf(i), 0) / Math.max(1, discretionary.length))),
    days: daysTo + 30,
    door: "two_way",
    metrics: [
      {
        label: t("cost"),
        value: t("{now} now, {escalation} of escalation on the rest", {
          now: money(costOf(mandatory), cur),
          escalation: money(slipCost(discretionary), cur),
        }),
        assumed: true,
      },
      { label: t("closure"), value: t("{n} h before the peak, none in it", { n: closureHoursOf(mandatory) }) },
      {
        label: t("exposure after"),
        value: t("{n} carried through the peak", {
          n: r1(discretionary.reduce((sum, i) => sum + exposureOf(i), 0)),
        }),
      },
      { label: t("door"), value: t("two-way — the frozen work resumes after") },
    ],
    displaces: discretionary.map((i) => i.asset),
    forCase: [t("No planned closure while the airport is fullest.")],
    againstCase: [
      t(
        "Discretionary findings drift through the peak; anything that fails then is an unplanned closure at the worst time.",
      ),
    ],
    sources: mandatory.flatMap((i) => i.cost.sources),
  };
  const options = [optSprint, optPlanned, optFreeze];
  const { key, reasoning, counter } = recommend(options, rate, cur);
  const draft: Draft = {
    id: "peak",
    frame: "peak",
    title: t("Ready for {peak}", { peak: peakName }),
    question: t(
      "{peak} starts in {n} days. Sprint the mandatory set, run the quarter as solved, or freeze airside works through it?",
      { peak: peakName, n: daysTo },
    ),
    onDesk: t("{findings} open at severity 4 or 5, {obligations} overdue{outages}.", {
      findings: tn(openHigh.length, "{n} finding", "{n} findings"),
      obligations: tn(overdue.length, "{n} obligation", "{n} obligations"),
      outages:
        outages?.value != null
          ? t(", {value} {unit} of major equipment out of service", { value: outages.value, unit: outages.unit })
          : "",
    }),
    decideBy: {
      days: Math.max(0, daysTo - 21),
      at: iso(inp.now, Math.max(0, daysTo - 21)),
      text: t("three weeks before the peak, so the windows can still be booked"),
      sources: assumedPeak ? [] : [enteredRef(e, t("peak dates"))],
    },
    horizon: { days: daysTo, text: t("before {peak} starts", { peak: peakName }) },
    door: "two_way",
    doorWhy: t("works can be re-sequenced up to the day; what cannot be undone is an unplanned closure in the peak"),
    stakes,
    options,
    recommended: key,
    reasoning,
    counter,
    ripples: [
      ...(crit?.known && crit.critical.length
        ? [
            t(
              "{positions} cannot be closed in the window without stranding traffic — any work there goes before or after",
              {
                positions: crit.critical.map((p) => p.position).join(t(" and ")),
              },
            ),
          ]
        : []),
      ...(outages?.value != null && outages.target != null && outages.value > outages.target
        ? [
            t(
              "equipment out of service is above target; the peak arrives with {value} {unit} down unless the repairs are in the sprint",
              { value: outages.value, unit: outages.unit },
            ),
          ]
        : []),
    ],
    evidence: [...stakes.flatMap((s) => s.sources), ...options.flatMap((o) => o.sources)],
    gaps,
  };
  return [finish(draft, inp, rate, inp.engine.constraints.budget)];
}

/** What goes in front of the regulator. */
function regulatorFrame(inp: RoomInputs): RoomDecision[] {
  const cur = inp.engine.rateCard.currency;
  const card = inp.engine.rateCard;
  const rate = card.closureCostPerHour.runway;
  const e = inp.entered;
  const auditAt = e.auditAt ?? iso(inp.now, 60);
  const assumed = e.auditAt == null;
  const daysTo = days(inp.now, auditAt);
  const comp = inp.compliance;
  const overdue = comp?.obligations.filter((o) => o.status === "overdue") ?? [];
  const pendingSoon =
    comp?.obligations.filter((o) => o.status === "pending" && o.dueAt && days(inp.now, o.dueAt) <= daysTo) ?? [];
  const crossed = inp.out.items.filter((i) => i.mandatory.is && i.condition.value != null && i.condition.limit != null);
  const mandatory = inp.out.portfolio.mandatory;
  const proposed = inp.kpis.filter((k) => k.targetStatus === "proposed").length;
  const costOf = (items: DecisionItem[]) => items.reduce((t, i) => t + (i.cost.now ?? 0), 0);
  const mitigationOf = (items: DecisionItem[]) =>
    items.reduce((t, i) => t + card.categories[i.category].mitigationCost, 0);
  const closureOf = (items: DecisionItem[]) => r1(items.reduce((t, i) => t + (i.surface.closureMinutes ?? 0) / 60, 0));
  const stakes: Stake[] = [
    {
      key: "days",
      label: t("days to the inspection"),
      value: daysTo,
      unit: t("days"),
      text: `${auditAt.slice(0, 10)}${assumed ? t(" — a starting value; enter the real date") : ""}`,
      assumed,
      sources: assumed ? [] : [enteredRef(e, t("inspection date"))],
    },
    ...(comp?.readiness ?? []).map((r) => ({
      key: `ready:${r.framework}`,
      label: r.framework,
      value: r.pct,
      unit: "%",
      text: t("{met} of {total} obligations met, {overdue} overdue", {
        met: r.met,
        total: r.total,
        overdue: r.overdue,
      }),
      sources: [{ kind: "obligation" as const, ref: r.framework, label: t("readiness"), href: "/compliance" }],
    })),
    {
      key: "crossed",
      label: t("limits crossed"),
      value: crossed.length,
      unit: "",
      text: crossed.map((i) => `${i.asset}: ${i.condition.label} (${i.clause.clause})`).join("; ") || t("none"),
      sources: crossed.flatMap((i) => i.clause.sources),
    },
    {
      key: "proposed",
      label: t("targets still proposed"),
      value: proposed,
      unit: "KPIs",
      text: proposed
        ? t("{n} KPI targets are proposed, not agreed — a grade against them is not a finding", { n: proposed })
        : t("every KPI target on the platform is agreed or from the pack"),
      sources: [],
    },
  ];
  const optClose: RoomOption = {
    key: "close",
    name: t("Close every gap first"),
    meaning: t("the {mandatory} mandatory items done and the {overdue} overdue obligations evidenced before {date}", {
      mandatory: mandatory.length,
      overdue: overdue.length,
      date: auditAt.slice(0, 10),
    }),
    cost: costOf(mandatory),
    closureHours: closureOf(mandatory),
    exposure: 0,
    days: Math.max(7, mandatory.length * 4),
    door: "two_way",
    metrics: [
      {
        label: t("cost"),
        value: money(costOf(mandatory), cur),
        assumed: mandatory.every((i) => i.cost.sources.every((s) => s.kind === "rate_card")),
      },
      { label: t("closure"), value: `${closureOf(mandatory)} h` },
      { label: t("exposure after"), value: "0" },
      {
        label: t("needs"),
        value: t("{n} night windows and the evidence filed", {
          n: mandatory.filter((i) => i.window.needsNight).length,
        }),
      },
    ],
    displaces: inp.out.portfolio.slipped.slice(0, 3).map((s) => s.item.asset),
    forCase: [t("The inspector finds a closed book.")],
    againstCase: [t("Takes the quarter's windows and crews off everything else.")],
    sources: mandatory.flatMap((i) => i.evidence).slice(0, 8),
  };
  const optDisclose: RoomOption = {
    key: "disclose",
    name: t("Disclose, with a dated plan and mitigations"),
    meaning: t("each open item declared with its mitigation in place and the date it closes"),
    cost: mitigationOf(mandatory),
    closureHours: 0,
    exposure: 0.4,
    days: 5,
    door: "two_way",
    metrics: [
      {
        label: t("cost"),
        value: t("{amount} of mitigations", { amount: money(mitigationOf(mandatory), cur) }),
        assumed: true,
      },
      { label: t("closure"), value: t("none before the inspection") },
      { label: t("exposure after"), value: t("0.4 — declared, mitigated, dated") },
      {
        label: t("mitigations"),
        value: [...new Set(mandatory.map((i) => card.categories[i.category].mitigation))].join("; "),
      },
    ],
    displaces: [],
    forCase: [t("A documented, dated, mitigated plan is what an inspector is trained to accept.")],
    againstCase: [t("An undertaking to the regulator is a hard date, and missing it is worse than not making it.")],
    sources: [rateRef(inp.engine.constraints, card, t("mitigation cost by category"))],
  };
  const optMinimum: RoomOption = {
    key: "minimum",
    name: t("The crossed limits only"),
    meaning: t("fix the {limits}; the overdue obligations are evidenced but not the rest", {
      limits: tn(crossed.length, "{n} crossed limit", "{n} crossed limits"),
    }),
    cost: costOf(crossed),
    closureHours: closureOf(crossed),
    exposure: overdue.length ? 0.6 : 0.2,
    days: 10,
    door: "two_way",
    metrics: [
      { label: t("cost"), value: money(costOf(crossed), cur), assumed: true },
      { label: t("closure"), value: `${closureOf(crossed)} h` },
      {
        label: t("exposure after"),
        value: t("{exposure} — {obligations} still open", {
          exposure: overdue.length ? 0.6 : 0.2,
          obligations: tn(overdue.length, "{n} overdue obligation", "{n} overdue obligations"),
        }),
      },
    ],
    displaces: [],
    forCase: [t("The finding that would ground a runway is gone; the rest is paperwork.")],
    againstCase: [t("An overdue obligation is a finding in its own right under Part 139.")],
    sources: crossed.flatMap((i) => i.clause.sources),
  };
  const options = [optClose, optDisclose, optMinimum];
  const { key, reasoning, counter } = recommend(options, rate, cur);
  const draft: Draft = {
    id: "regulator",
    frame: "regulator",
    title: t("In front of the regulator"),
    question: t(
      "An inspection in {n} days. Close every gap first, disclose with a dated plan, or fix the crossed limits only?",
      { n: daysTo },
    ),
    onDesk: t("{limits} crossed, {obligations} overdue, {n} due before the inspection.", {
      limits: tn(crossed.length, "{n} limit", "{n} limits"),
      obligations: tn(overdue.length, "{n} obligation", "{n} obligations"),
      n: pendingSoon.length,
    }),
    decideBy: {
      days: Math.max(0, daysTo - 14),
      at: iso(inp.now, Math.max(0, daysTo - 14)),
      text: t("a fortnight before, so what is promised can be evidenced"),
      sources: assumed ? [] : [enteredRef(e, t("inspection date"))],
    },
    horizon: { days: daysTo, text: t("before the inspection") },
    door: "two_way",
    doorWhy: t("nothing here is irreversible except an undertaking given in writing"),
    stakes,
    options,
    recommended: key,
    reasoning,
    counter,
    ripples: [
      ...(pendingSoon.length
        ? [
            t("{items} fall due before the inspection and join the list if missed", {
              items: pendingSoon.map((o) => `${o.title} (${o.dueAt?.slice(0, 10)})`).join("; "),
            }),
          ]
        : []),
      ...(proposed
        ? [
            t("{n} proposed KPI targets should be agreed or withdrawn before an inspector reads a grade against them", {
              n: proposed,
            }),
          ]
        : []),
    ],
    evidence: [...stakes.flatMap((s) => s.sources), ...options.flatMap((o) => o.sources)],
    gaps: comp ? [] : [t("the compliance register is not on the platform")],
  };
  return [finish(draft, inp, rate, inp.engine.constraints.budget)];
}

/** The quarter's windows: the solver's answer, and two others. */
function quarterFrame(inp: RoomInputs): RoomDecision[] {
  const cur = inp.engine.rateCard.currency;
  const card = inp.engine.rateCard;
  const rate = card.closureCostPerHour.taxiway;
  const c = inp.engine.constraints;
  const run = (
    name: string,
    key: string,
    weights: Partial<Constraints["weights"]>,
    meaning: string,
    forCase: string,
    againstCase: string,
  ): RoomOption => {
    const out =
      key === "solver"
        ? inp.out
        : runEngine({ ...inp.engine, constraints: { ...c, weights: { ...c.weights, ...weights } } });
    const p = out.portfolio;
    const chosen = p.chosen.filter((i) => !i.mandatory.is);
    const closure = r1(p.chosen.reduce((t, i) => t + (i.surface.closureMinutes ?? 0) / 60, 0));
    const retired = r1(chosen.reduce((t, i) => t + exposureOf(i), 0));
    const left = r1(p.slipped.reduce((t, s) => t + exposureOf(s.item), 0));
    const escalationAvoided = chosen.reduce((t, i) => t + ((i.cost.quarter ?? i.cost.now ?? 0) - (i.cost.now ?? 0)), 0);
    return {
      key,
      name,
      meaning: t("{meaning}: {chosen} discretionary items after the {mandatory} mandatory", {
        meaning,
        chosen: chosen.length,
        mandatory: p.mandatory.length,
      }),
      cost: Math.max(0, p.used.budget - escalationAvoided),
      closureHours: closure,
      exposure: r1(Math.min(1, left / Math.max(1, left + retired))),
      days: 90,
      door: "two_way",
      metrics: [
        {
          label: t("spend"),
          value: t("{used} of {available}, {avoided} of escalation avoided", {
            used: money(p.used.budget, cur),
            available: money(p.available.budget, cur),
            avoided: money(escalationAvoided, cur),
          }),
        },
        { label: t("windows"), value: t("{used} of {total}", { used: p.used.windows, total: p.available.windows }) },
        { label: t("closure"), value: `${closure} h` },
        {
          label: t("exposure"),
          value: t("{retired} retired, {left} left on the slipped set", { retired, left }),
        },
        { label: t("slips"), value: p.slipped.map((s) => s.item.asset).join(", ") || t("nothing") },
      ],
      displaces: p.slipped.map((s) => s.item.asset),
      forCase: [forCase],
      againstCase: [p.infeasible ?? againstCase],
      sources: [{ kind: "entered", ref: c.quarter, label: t("the quarter's constraints"), enteredBy: card.enteredBy }],
    };
  };
  const options = [
    run(
      t("The solver's set"),
      "solver",
      {},
      t("the default weights"),
      t("The best value per window, budget and crew-night on the weights as set."),
      t("It is only as good as the weights, which are the executive's to move."),
    ),
    run(
      t("Protect availability"),
      "availability",
      { consequence: 0.5, regulatoryExposure: 0.1 },
      t("consequence weighted to 0.5"),
      t("Runway and taxiway hours come first; the airfield stays open."),
      t("Regulatory findings wait."),
    ),
    run(
      t("Retire exposure"),
      "exposure",
      { regulatoryExposure: 0.5, consequence: 0.1 },
      t("regulatory exposure weighted to 0.5"),
      t("Every clause-bearing finding is retired first."),
      t("Availability is spent on it."),
    ),
  ];
  const { key, reasoning, counter } = recommend(options, rate, cur);
  const p = inp.out.portfolio;
  const stakes: Stake[] = [
    {
      key: "windows",
      label: t("night windows"),
      value: p.available.windows,
      unit: "",
      text: t("{n} this quarter, {taken} taken by the mandatory set", {
        n: p.available.windows,
        taken: p.mandatory.filter((i) => i.window.needsNight).length,
      }),
      sources: [{ kind: "entered", ref: c.quarter, label: t("the quarter") }],
    },
    {
      key: "budget",
      label: t("budget"),
      value: p.available.budget,
      unit: cur,
      text: t("{amount}; the mandatory set alone is {mandatory}", {
        amount: money(p.available.budget, cur),
        mandatory: money(
          p.mandatory.reduce((sum, i) => sum + (i.cost.now ?? 0), 0),
          cur,
        ),
      }),
      sources: [{ kind: "entered", ref: c.quarter, label: t("the quarter") }],
    },
    {
      key: "items",
      label: t("findings in play"),
      value: inp.out.items.length,
      unit: "",
      text: t("{mandatory} mandatory, {discretionary} discretionary", {
        mandatory: p.mandatory.length,
        discretionary: inp.out.items.length - p.mandatory.length,
      }),
      sources: [],
    },
    { key: "confidence", label: t("confidence"), value: null, unit: "", text: inp.out.confidence.note, sources: [] },
  ];
  const draft: Draft = {
    id: `quarter:${c.quarter}`,
    frame: "quarter",
    title: t("The windows of {quarter}", { quarter: c.quarter }),
    question: t("{n} night windows and {amount}: the solver's set, protect availability, or retire exposure first?", {
      n: p.available.windows,
      amount: money(p.available.budget, cur),
    }),
    onDesk: t("{mandatory} items are mandatory and pre-committed; {rest} compete for what is left.", {
      mandatory: p.mandatory.length,
      rest: inp.out.items.length - p.mandatory.length,
    }),
    decideBy: {
      days: 14,
      at: iso(inp.now, 14),
      text: t("before the first window of the quarter is booked"),
      sources: [],
    },
    horizon: { days: 90, text: t("the quarter") },
    door: "two_way",
    doorWhy: t("the weights and the constraints can be moved and the quarter re-solved at any time"),
    stakes,
    options,
    recommended: key,
    reasoning: [
      ...reasoning,
      t(
        "Cash-equivalent here is spend less the escalation avoided, plus the closure hours priced at the taxiway rate.",
      ),
    ],
    counter,
    ripples: [
      ...(p.infeasible ? [p.infeasible] : []),
      t("{items} slip under the solver's set{detail}", {
        items: tn(p.slipped.length, "{n} item", "{n} items"),
        detail: p.slipped.length
          ? `: ${p.slipped.map((s) => `${s.item.asset}${s.slipCost != null ? t(" ({amount} to slip)", { amount: money(s.slipCost, cur) }) : ""}`).join("; ")}`
          : "",
      }),
    ],
    evidence: inp.out.items.flatMap((i) => i.evidence).slice(0, 20),
    gaps: [],
  };
  return [finish(draft, inp, rate, c.budget)];
}

/* ── the desk ─────────────────────────────────────────────────────────── */

export function buildDesk(inp: RoomInputs): Desk {
  const decisions = [
    ...runwayFrame(inp),
    ...peakFrame(inp),
    ...regulatorFrame(inp),
    ...capitalFrame(inp),
    ...quarterFrame(inp),
  ].sort((a, b) => (a.decideBy.days ?? 9999) - (b.decideBy.days ?? 9999));
  const cur = inp.engine.rateCard.currency;
  const figures = new Set<string>();
  const add = (n: number | null | undefined) => {
    if (n == null || Number.isNaN(n)) return;
    figures.add(String(Math.round(n)));
    figures.add(String(r1(n)));
    figures.add(Math.round(n).toLocaleString("en"));
    figures.add(String(n));
  };
  for (const d of decisions) {
    add(d.decideBy.days);
    for (const s of d.stakes) add(s.value);
    for (const o of d.options) {
      add(o.cost);
      add(o.closureHours);
      add(o.exposure);
      add(o.days);
    }
    for (const c of d.stress) add(c.ratio);
    for (const s of d.scenarios) {
      add(s.budgetCap);
      add(s.addClosureHours);
      add(s.addDays);
    }
  }
  for (const d of decisions)
    for (const n of materialForRoom(d, cur).match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
      figures.add(n);
      figures.add(n.replace(/,/g, ""));
    }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  figures.add(String(inp.now.getFullYear()));
  const money_ = decisions.reduce((t, d) => {
    const rec = d.options.find((o) => o.key === d.recommended);
    return t + (rec?.cost ?? 0);
  }, 0);
  return {
    generatedAt: inp.now.toISOString(),
    siteCode: inp.siteCode,
    currency: cur,
    decisions,
    summary: {
      open: decisions.length,
      oneWay: decisions.filter((d) => d.door === "one_way").length,
      nearestDays: decisions.length ? (decisions[0].decideBy.days ?? null) : null,
      moneyInPlay: money_,
    },
    figures,
  };
}

/* ── the model layer ──────────────────────────────────────────────────── */

export const ROOM_MODEL_PROMPT = [
  "You are advising an airport's chief executive on one decision, from the material given and nothing else.",
  "You may explain the options, argue for one, argue against the recommendation, and answer questions.",
  "You must not state any number that is not in the material. Do not estimate, extrapolate or round differently.",
  "Say plainly when a figure rests on a rate card or an entered value rather than a platform record. Be brief and direct.",
].join(" ");

export function materialForRoom(d: RoomDecision, currency: string): string {
  const L: string[] = [
    `Decision: ${d.title}. ${d.question} On the desk because: ${d.onDesk} Decide by: ${d.decideBy.text} (${d.decideBy.days ?? "n/a"} days). Horizon: ${d.horizon.text} (${d.horizon.days ?? "n/a"} days). Door: ${d.door.replace("_", "-")} — ${d.doorWhy}.`,
  ];
  for (const s of d.stakes)
    L.push(`Stake ${s.label}: ${s.value ?? "n/a"} ${s.unit} — ${s.text}${s.assumed ? " [assumed]" : ""}`);
  for (const o of d.options) {
    L.push(
      `Option ${o.key} "${o.name}": ${o.meaning}. Cost ${o.cost ?? "not priced"} ${currency}; closure ${o.closureHours ?? "n/a"} h; exposure after ${o.exposure}; ${o.days ?? "n/a"} days; ${o.door.replace("_", "-")} door.${o.displaces.length ? ` Displaces ${o.displaces.join(", ")}.` : ""} For: ${o.forCase.join(" ")} Against: ${o.againstCase.join(" ")}`,
    );
    for (const m of o.metrics) L.push(`  ${m.label}: ${m.value}${m.assumed ? " [assumed]" : ""}`);
  }
  L.push(`Recommended: ${d.recommended ?? "none"}. ${d.reasoning.join(" ")} Counter: ${d.counter}`);
  L.push(`Stress test — robust: ${d.robust ?? "none"}. ${d.robustNote}`);
  for (const s of d.scenarios)
    L.push(
      `Scenario ${s.key} "${s.name}": ${s.what}. ${d.stress
        .filter((c) => c.scenario === s.key)
        .map((c) => `${c.option}: ${c.verdict} (${c.text})`)
        .join("; ")}`,
    );
  for (const f of d.flips) L.push(`Flip: ${f.input} is ${f.now}; ${f.at}, ${f.outcome}.`);
  for (const r of d.ripples) L.push(`Ripple: ${r}`);
  for (const g of d.gaps) L.push(`Gap: ${g}`);
  return L.join("\n");
}

/** Questions the room can answer itself, without a model. */
export function answerInRoom(q: string, d: RoomDecision, currency: string): string | null {
  const ql = q.toLowerCase();
  if (/robust|safest|survive|stress/.test(ql)) return d.robustNote;
  if (/flip|change (my|the) (mind|answer)|what would/.test(ql))
    return d.flips.length
      ? d.flips
          .map((f) =>
            t("{input} is {now}; {at}, {outcome}.", {
              input: `${f.input[0].toUpperCase()}${f.input.slice(1)}`,
              now: f.now,
              at: f.at,
              outcome: f.outcome,
            }),
          )
          .join(" ")
      : t("No flip point: the recommendation holds across every sweep the room tried, or no second option is priced.");
  if (/why|reason|recommend/.test(ql)) return d.reasoning.join(" ");
  if (/against|counter|argue/.test(ql)) return d.counter || t("No counter-argument: only one option is priced.");
  if (/displace|push|slip/.test(ql)) {
    const rec = d.options.find((o) => o.key === d.recommended);
    return rec
      ? rec.displaces.length
        ? t("{name} displaces {items}.", { name: rec.name, items: rec.displaces.join(", ") })
        : t("{name} displaces nothing on the platform's records.", { name: rec.name })
      : null;
  }
  if (/board|one line|summar/.test(ql)) return d.boardLine.join(" ");
  const opt = d.options.find((o) => ql.includes(o.name.toLowerCase().slice(0, 12)) || ql.includes(o.key));
  if (opt)
    return t("{name}: {meaning}. {cost}{closure}, exposure {exposure} after. For: {for} Against: {against}", {
      name: opt.name,
      meaning: opt.meaning,
      cost: opt.cost != null ? money(r0(opt.cost), currency) : t("Not priced"),
      closure: opt.closureHours != null ? t(", {n} h of closure", { n: opt.closureHours }) : "",
      exposure: opt.exposure,
      for: opt.forCase.join(" "),
      against: opt.againstCase.join(" "),
    });
  return null;
}
